import {
  homeResponseSchema,
  landingResponseSchema,
  workBookmarkMutationResponseSchema,
  type HomeResponse,
  type HomeWorkCard,
  type LandingResponse,
  emptyWorkSourceProvenance,
  type SourceLanguage
} from "@loomua/contracts";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createApiClient } from "./apiClient";
import {
  getBrowserAccessToken,
  navigateBrowser,
  signOutBrowserSession
} from "./auth";
import { toSimplifiedChineseText } from "./chineseText";
import { Icon } from "./icons";
import { useInterfaceLanguage } from "./interfaceLanguage";
import { workCoverViewTransitionStyle } from "./motion";
import { MotionFadeThrough } from "./motionComponents";
import { WorkCover } from "./WorkCover";
import {
  WorkTicketBadges,
  WorkTicketCreator,
  WorkTicketStats,
  workTicketDisplayText
} from "./WorkTicket";

type FandomGroup = {
  id: string;
  name: string;
  detailHref?: string;
  works: HomeWorkCard[];
};

type GenreFilter = {
  id: string;
  label: string;
  aliases: string[];
  count: number;
};

type PopularGenreFilter = {
  id: string;
  en: string;
  zh: string;
  aliases: string[];
  matches?: (work: HomeWorkCard) => boolean;
};

const FANDOM_RAIL_LIMIT = 18;
const FANDOM_CHIP_TONE_COUNT = 8;
const GENRE_FILTER_LIMIT = 14;
const COMPACT_TAG_LIMIT = 5;
const HOME_WORK_LIMIT = 9;
const EMPTY_HOME_WORKS: HomeWorkCard[] = [];
type HomePageMode = "authenticated" | "public";
const POPULAR_GENRE_FILTERS: PopularGenreFilter[] = [
  {
    id: "relationship",
    en: "Relationship",
    zh: "CP向",
    aliases: ["relationship", "ship", "cp", "cp向", "CP向"],
    matches: (work) => work.ships.length > 0
  },
  {
    id: "friendship",
    en: "Friendship",
    zh: "友情向",
    aliases: ["friendship", "友情", "友情向", "朋友"]
  },
  {
    id: "ensemble",
    en: "Ensemble",
    zh: "群像",
    aliases: ["ensemble", "群像", "多主角"]
  },
  {
    id: "canon-compliant",
    en: "Canon",
    zh: "原著向",
    aliases: ["canon", "canon compliant", "canon-compliant", "原著向"]
  },
  {
    id: "alternate-universe",
    en: "AU",
    zh: "AU",
    aliases: ["au", "alternate universe", "平行世界", "架空", "现代 AU"]
  },
  {
    id: "one-shot",
    en: "One-shot",
    zh: "短篇",
    aliases: ["one-shot", "oneshot", "short-work", "short work", "短篇", "短文"]
  },
  {
    id: "series",
    en: "Series",
    zh: "长篇",
    aliases: ["series", "longfic", "long-form", "长篇", "连载长篇"]
  },
  {
    id: "ongoing",
    en: "Ongoing",
    zh: "连载",
    aliases: ["ongoing", "连载", "连载中"],
    matches: (work) => work.status === "ongoing"
  },
  {
    id: "completed",
    en: "Completed",
    zh: "完结",
    aliases: ["completed", "complete", "已完结", "完结"],
    matches: (work) => work.status === "completed"
  },
  {
    id: "light",
    en: "Light",
    zh: "轻松",
    aliases: ["light", "fluff", "comedy", "humor", "轻松", "轻喜剧", "搞笑"]
  },
  {
    id: "healing",
    en: "Healing",
    zh: "治愈",
    aliases: ["healing", "hurt/comfort", "hurt comfort", "伤痛治愈", "治愈"]
  },
  {
    id: "drama",
    en: "Drama",
    zh: "剧情",
    aliases: ["drama", "plot", "剧情"]
  },
  {
    id: "mystery",
    en: "Mystery",
    zh: "悬疑",
    aliases: ["mystery", "悬疑", "推理"]
  },
  {
    id: "fantasy",
    en: "Fantasy",
    zh: "奇幻",
    aliases: ["fantasy", "奇幻", "魔法"]
  },
  {
    id: "school",
    en: "School",
    zh: "校园",
    aliases: ["school", "校园", "学院"]
  }
];

function homeFailureDetails(error: unknown) {
  if (!(error instanceof Error)) return null;
  const maybeApiError = error as Error & {
    code?: string;
    requestId?: string;
    status?: number;
  };
  const details = [
    maybeApiError.status ? `HTTP ${maybeApiError.status}` : null,
    maybeApiError.code ?? null,
    maybeApiError.requestId ? `Request ${maybeApiError.requestId}` : null
  ].filter(Boolean);
  return details.length ? details.join(" · ") : error.message;
}

function isAbortError(error: unknown) {
  return error instanceof Error && error.name === "AbortError";
}

function isAuthRequiredError(error: unknown) {
  if (!(error instanceof Error)) return false;
  const maybeApiError = error as Error & { code?: string; status?: number };
  return maybeApiError.status === 401 && maybeApiError.code === "AUTH_REQUIRED";
}

function groupWorksByFandom(works: HomeWorkCard[]) {
  return Array.from(
    works
      .reduce((groups, work) => {
        const group = groups.get(work.fandom.id);
        if (group) {
          group.works.push(work);
        } else {
          groups.set(work.fandom.id, {
            id: work.fandom.id,
            name: work.fandom.name,
            works: [work]
          });
        }
        return groups;
      }, new Map<string, FandomGroup>())
      .values()
  );
}

function popularFandomGroups(groups: FandomGroup[]) {
  return [...groups]
    .sort((first, second) => {
      const popularity = second.works.length - first.works.length;
      return popularity || first.name.localeCompare(second.name);
    })
    .slice(0, FANDOM_RAIL_LIMIT);
}

function genreKey(label: string) {
  return toSimplifiedChineseText(label.trim()).toLocaleLowerCase();
}

function buildGenreFilters(works: HomeWorkCard[], zh: boolean): GenreFilter[] {
  return POPULAR_GENRE_FILTERS.map((genre) => ({
    id: genre.id,
    label: zh ? genre.zh : genre.en,
    aliases: genre.aliases.map(genreKey),
    count: works.filter((work) => workMatchesPopularGenre(work, genre)).length
  })).slice(0, GENRE_FILTER_LIMIT);
}

function workMatchesPopularGenre(
  work: HomeWorkCard,
  filter: PopularGenreFilter
) {
  if (filter.matches?.(work)) return true;
  const aliases = filter.aliases.map(genreKey);
  return work.tags.some((tag) => aliases.includes(genreKey(tag)));
}

function workMatchesGenre(work: HomeWorkCard, selectedGenre: string) {
  if (selectedGenre === "all") return true;
  const selectedFilter = POPULAR_GENRE_FILTERS.find(
    (genre) => genre.id === selectedGenre
  );
  if (selectedFilter) return workMatchesPopularGenre(work, selectedFilter);
  return work.tags.some((tag) => genreKey(tag) === genreKey(selectedGenre));
}

function homeDisplayText(text: string, language: SourceLanguage) {
  return workTicketDisplayText(text, language);
}

function publicLandingToHome(
  landing: LandingResponse,
  displayLanguage: SourceLanguage
): HomeResponse {
  const works = landing.contents.flatMap((content) => {
    const fandom = landing.fandoms.find(
      (entry) => entry.id === content.fandomId
    );
    const ship = content.shipId
      ? landing.ships.find((entry) => entry.id === content.shipId)
      : undefined;
    if (
      !fandom ||
      !content.creator ||
      !content.sourceLanguage ||
      !content.status
    ) {
      return [];
    }
    return [
      {
        id: content.id,
        title: content.title,
        summary: content.summary || content.excerpt || null,
        creator: content.creator,
        creatorAvatarUrl: content.creatorAvatarUrl,
        fandom: { id: fandom.id, name: fandom.name },
        ships: ship ? [{ id: ship.id, label: ship.label }] : [],
        sourceLanguage: content.sourceLanguage,
        translationState: "available" as const,
        status: content.status,
        rating: content.ratingLabels[0] ?? null,
        warnings: content.warningLabels,
        tags: content.tags ?? [],
        ticketBadges: content.ticketBadges,
        stats: content.stats,
        source: content.source ?? emptyWorkSourceProvenance,
        cover: content.cover ?? null,
        detailHref: content.detailHref
      }
    ];
  });
  return homeResponseSchema.parse({ displayLanguage, works });
}

function publicLandingToFandomGroups(
  landing: LandingResponse,
  works: HomeWorkCard[]
): FandomGroup[] {
  const worksByFandom = new Map(
    groupWorksByFandom(works).map((group) => [group.id, group.works])
  );
  return landing.fandoms.map((fandom) => ({
    id: fandom.id,
    name: fandom.name,
    detailHref: fandom.detailHref,
    works: worksByFandom.get(fandom.id) ?? []
  }));
}

async function loadPublicHome(language: SourceLanguage, signal: AbortSignal) {
  const landing = await createApiClient().request(
    `/api/v1/landing?displayLanguage=${encodeURIComponent(language)}`,
    {
      schema: landingResponseSchema,
      signal
    }
  );
  const nextHome = publicLandingToHome(landing, language);
  return {
    fandomGroups: publicLandingToFandomGroups(landing, nextHome.works),
    home: nextHome
  };
}

type HorizontalWheelEvent = {
  cancelable?: boolean;
  deltaMode?: number;
  deltaX?: number;
  deltaY?: number;
  preventDefault: () => void;
  stopPropagation: () => void;
  wheelDeltaX?: number;
};

function horizontalWheelDelta(event: HorizontalWheelEvent, pageWidth: number) {
  if (typeof event.deltaX === "number" && event.deltaX !== 0) {
    const unit =
      event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? pageWidth : 1;
    return event.deltaX * unit;
  }
  if (typeof event.wheelDeltaX === "number" && event.wheelDeltaX !== 0) {
    return -event.wheelDeltaX;
  }
  if (typeof event.deltaY === "number" && event.deltaY !== 0) {
    const unit =
      event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? pageWidth : 1;
    return event.deltaY * unit;
  }
  return 0;
}

function applyHorizontalWheel(
  railElement: HTMLElement,
  event: HorizontalWheelEvent
) {
  const maxScrollLeft = railElement.scrollWidth - railElement.clientWidth;
  if (maxScrollLeft <= 0) return;
  const delta = horizontalWheelDelta(event, railElement.clientWidth);
  if (delta === 0) return;
  const nextScrollLeft = Math.min(
    Math.max(railElement.scrollLeft + delta, 0),
    maxScrollLeft
  );
  if (nextScrollLeft === railElement.scrollLeft) return;
  event.preventDefault();
  event.stopPropagation();
  railElement.scrollLeft = nextScrollLeft;
}

function FandomShortcutRail({
  groups,
  preserveOrder = false,
  zh,
  language
}: {
  groups: FandomGroup[];
  preserveOrder?: boolean;
  zh: boolean;
  language: SourceLanguage;
}) {
  const shortcuts = preserveOrder
    ? groups.slice(0, FANDOM_RAIL_LIMIT)
    : popularFandomGroups(groups);
  const railRef = useRef<HTMLElement | null>(null);
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false
  });

  function updateScrollState() {
    const rail = railRef.current;
    if (!rail) return;
    const maxScrollLeft = rail.scrollWidth - rail.clientWidth;
    const nextScrollState = {
      canScrollLeft: rail.scrollLeft > 2,
      canScrollRight: rail.scrollLeft < maxScrollLeft - 2
    };
    setScrollState((current) => {
      if (
        current.canScrollLeft === nextScrollState.canScrollLeft &&
        current.canScrollRight === nextScrollState.canScrollRight
      ) {
        return current;
      }
      return nextScrollState;
    });
  }

  function scrollRail(direction: -1 | 1) {
    const rail = railRef.current;
    if (!rail) return;
    rail.scrollBy({
      left: direction * Math.max(rail.clientWidth * 0.75, 220),
      behavior: "smooth"
    });
    window.setTimeout(updateScrollState, 260);
  }

  useLayoutEffect(() => {
    updateScrollState();
    const rail = railRef.current;
    if (!rail) return;
    const railElement = rail;
    function handleWheel(rawEvent: Event) {
      const event = rawEvent as HorizontalWheelEvent;
      applyHorizontalWheel(railElement, event);
      updateScrollState();
    }
    railElement.addEventListener("wheel", handleWheel, { passive: false });
    railElement.addEventListener("mousewheel", handleWheel, {
      passive: false
    });
    const BrowserResizeObserver = (
      globalThis as { ResizeObserver?: typeof ResizeObserver }
    ).ResizeObserver;
    if (BrowserResizeObserver) {
      const observer = new BrowserResizeObserver(updateScrollState);
      observer.observe(rail);
      return () => {
        observer.disconnect();
        railElement.removeEventListener("wheel", handleWheel);
        railElement.removeEventListener("mousewheel", handleWheel);
      };
    }
    globalThis.addEventListener("resize", updateScrollState);
    return () => {
      globalThis.removeEventListener("resize", updateScrollState);
      railElement.removeEventListener("wheel", handleWheel);
      railElement.removeEventListener("mousewheel", handleWheel);
    };
  }, [shortcuts.length]);

  if (shortcuts.length === 0) return null;

  return (
    <section
      className="fandom-shortcut-section"
      aria-label={zh ? "IP 快捷入口" : "Fandom shortcuts"}
    >
      <div className="fandom-shortcut-frame">
        <button
          className="fandom-shortcut-scroll is-left"
          type="button"
          aria-label={zh ? "向左滚动 IP" : "Scroll fandoms left"}
          disabled={!scrollState.canScrollLeft}
          onClick={() => scrollRail(-1)}
        >
          <Icon name="back" size={20} />
        </button>
        <nav
          className="fandom-shortcut-rail"
          aria-label={zh ? "IP 快捷入口" : "Fandom shortcuts"}
          ref={railRef}
          onScroll={updateScrollState}
        >
          {shortcuts.map((group, index) => {
            const label = homeDisplayText(group.name, language);
            return (
              <a
                className={`fandom-chip is-tone-${index % FANDOM_CHIP_TONE_COUNT}`}
                data-native-navigation={group.detailHref ? "true" : undefined}
                href={group.detailHref ?? `/fandoms/${group.id}`}
                key={group.id}
              >
                <span className="fandom-chip-title">#{label}</span>
              </a>
            );
          })}
        </nav>
        <button
          className="fandom-shortcut-scroll is-right"
          type="button"
          aria-label={zh ? "向右滚动 IP" : "Scroll fandoms right"}
          disabled={!scrollState.canScrollRight}
          onClick={() => scrollRail(1)}
        >
          <Icon name="right" size={20} />
        </button>
      </div>
    </section>
  );
}

function GenreFilterBar({
  filters,
  selectedGenre,
  setSelectedGenre,
  zh
}: {
  filters: GenreFilter[];
  selectedGenre: string;
  setSelectedGenre: (genre: string) => void;
  zh: boolean;
}) {
  const headingId = "genre-filter-title";
  return (
    <section className="genre-filter-section" aria-labelledby={headingId}>
      <h2 id={headingId}>{zh ? "题材" : "Genre"}</h2>
      <div className="genre-filter-bar" role="list">
        <button
          aria-pressed={selectedGenre === "all"}
          className="genre-filter-chip"
          onClick={() => setSelectedGenre("all")}
          type="button"
        >
          {zh ? "全部题材" : "All Genres"}
        </button>
        {filters.map((filter) => (
          <button
            aria-pressed={selectedGenre === filter.id}
            className="genre-filter-chip"
            key={filter.id}
            onClick={() => setSelectedGenre(filter.id)}
            type="button"
          >
            {filter.label}
          </button>
        ))}
      </div>
    </section>
  );
}

function CompactWorkCard({
  work,
  zh,
  language,
  mode
}: {
  work: HomeWorkCard;
  zh: boolean;
  language: SourceLanguage;
  mode: HomePageMode;
}) {
  const [bookmarked, setBookmarked] = useState(false);
  const [busy, setBusy] = useState(false);
  const title = homeDisplayText(work.title, language);
  const summary = work.summary ? homeDisplayText(work.summary, language) : null;
  const shipLabels = new Set(
    work.ships.map((ship) =>
      homeDisplayText(ship.label, language).trim().toLocaleLowerCase()
    )
  );
  const dedupedTags = work.tags.filter(
    (tag) =>
      !shipLabels.has(homeDisplayText(tag, language).trim().toLocaleLowerCase())
  );
  const visibleTags = dedupedTags.slice(0, COMPACT_TAG_LIMIT);
  const hiddenTagCount = Math.max(0, dedupedTags.length - visibleTags.length);
  const readHref =
    mode === "public"
      ? (work.detailHref ?? `/public/works/${work.id}`)
      : `/works/${work.id}/read`;
  const nativeReadNavigation =
    mode === "public" && work.detailHref ? "true" : undefined;

  async function toggleBookmark() {
    if (busy) return;
    if (mode === "public") {
      navigateBrowser(
        `/login?returnTo=${encodeURIComponent(`/works/${work.id}/read`)}`
      );
      return;
    }
    const nextBookmarked = !bookmarked;
    setBusy(true);
    try {
      const response = await createApiClient({
        getAccessToken: getBrowserAccessToken
      }).request(`/api/v1/works/${work.id}/bookmark`, {
        method: nextBookmarked ? "PUT" : "DELETE",
        schema: workBookmarkMutationResponseSchema
      });
      setBookmarked(response.bookmarked);
    } catch {
      setBookmarked(bookmarked);
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="compact-work-card">
      <a
        className="compact-work-cover"
        data-native-navigation={nativeReadNavigation}
        href={readHref}
        style={workCoverViewTransitionStyle(work.id)}
      >
        <WorkCover cover={work.cover} title={title} />
      </a>
      <div className="compact-work-body">
        <WorkTicketBadges
          className="compact-work-badges work-ticket-badges"
          language={language}
          work={work}
        />
        <h3 className="compact-work-title">
          <a data-native-navigation={nativeReadNavigation} href={readHref}>
            {title}
          </a>
        </h3>
        <WorkTicketCreator
          className="compact-work-creator work-ticket-creator"
          language={language}
          work={work}
        />
        <p className="compact-work-tags">
          {work.ships.map((ship) => {
            const label = homeDisplayText(ship.label, language);
            return (
              <a href={`/search?q=${encodeURIComponent(label)}`} key={ship.id}>
                #{label}
              </a>
            );
          })}
          {visibleTags.map((tag) => {
            const label = homeDisplayText(tag, language);
            return (
              <a href={`/search?q=${encodeURIComponent(label)}`} key={tag}>
                #{label}
              </a>
            );
          })}
          {hiddenTagCount ? <span>+{hiddenTagCount}</span> : null}
        </p>
        {summary ? <p className="compact-work-summary">{summary}</p> : null}
        <WorkTicketStats
          className="compact-work-stats work-ticket-stats"
          language={language}
          work={work}
        />
      </div>
      <button
        aria-label={
          bookmarked
            ? zh
              ? "移出书架"
              : "Remove bookmark"
            : mode === "public"
              ? zh
                ? "登录后加入书架"
                : "Sign in to bookmark"
              : zh
                ? "加入书架"
                : "Bookmark"
        }
        className={`compact-work-save ${bookmarked ? "is-saved" : ""}`}
        disabled={busy}
        onClick={() => void toggleBookmark()}
        title={bookmarked ? (zh ? "已加入书架" : "Bookmarked") : undefined}
        type="button"
      >
        <Icon name="favorite" />
      </button>
    </article>
  );
}

function WorkListSection({
  works,
  zh,
  language,
  mode
}: {
  works: HomeWorkCard[];
  zh: boolean;
  language: SourceLanguage;
  mode: HomePageMode;
}) {
  const headingId = "popular-works-title";
  const visibleWorks = works.slice(0, HOME_WORK_LIMIT);
  const hasMore = works.length > visibleWorks.length;
  return (
    <section className="work-list-section" aria-labelledby={headingId}>
      <header className="work-list-heading">
        <h2 id={headingId}>{zh ? "热门作品" : "Popular works"}</h2>
        {hasMore ? <a href="/search">{zh ? "查看全部" : "Show all"}</a> : null}
      </header>
      <MotionFadeThrough
        motionKey={`${language}:${visibleWorks.map((work) => work.id).join("|")}`}
        className="work-list-motion"
      >
        {works.length ? (
          <div className="compact-work-grid">
            {visibleWorks.map((work) => (
              <CompactWorkCard
                key={work.id}
                language={language}
                mode={mode}
                work={work}
                zh={zh}
              />
            ))}
          </div>
        ) : (
          <p className="work-list-empty">
            {zh ? "这个题材暂时没有作品。" : "No works in this genre yet."}
          </p>
        )}
      </MotionFadeThrough>
    </section>
  );
}

export function HomePage({ mode = "authenticated" }: { mode?: HomePageMode }) {
  const { language } = useInterfaceLanguage();
  const zh = language === "zh-CN";
  const [home, setHome] = useState<HomeResponse>();
  const [failure, setFailure] = useState<unknown>();
  const [publicFandomGroups, setPublicFandomGroups] = useState<FandomGroup[]>(
    []
  );
  const [selectedGenre, setSelectedGenre] = useState("all");
  const works = home?.works ?? EMPTY_HOME_WORKS;
  const fandomGroups = useMemo(() => groupWorksByFandom(works), [works]);
  const shortcutGroups =
    mode === "public" && publicFandomGroups.length
      ? publicFandomGroups
      : fandomGroups;
  const genreFilters = useMemo(() => buildGenreFilters(works, zh), [works, zh]);
  const visibleWorks = useMemo(
    () => works.filter((work) => workMatchesGenre(work, selectedGenre)),
    [works, selectedGenre]
  );

  useEffect(() => {
    const controller = new AbortController();
    setFailure(undefined);
    setHome(undefined);
    setPublicFandomGroups([]);

    const loadPublicFallback = () =>
      loadPublicHome(language, controller.signal)
        .then((next) => {
          if (controller.signal.aborted) return;
          setPublicFandomGroups(next.fandomGroups);
          setHome(next.home);
        })
        .catch((error: unknown) => {
          if (!isAbortError(error)) setFailure(error);
        });

    if (mode === "authenticated") {
      void createApiClient({ getAccessToken: getBrowserAccessToken })
        .request(
          `/api/v1/home?displayLanguage=${encodeURIComponent(language)}`,
          {
            schema: homeResponseSchema,
            signal: controller.signal
          }
        )
        .then(setHome)
        .catch((error: unknown) => {
          if (isAbortError(error)) return;
          if (isAuthRequiredError(error)) {
            void signOutBrowserSession();
            void loadPublicFallback();
            return;
          }
          setFailure(error);
        });
    } else {
      void loadPublicFallback();
    }
    return () => controller.abort();
  }, [language, mode]);

  if (!home) {
    const failureDetails = homeFailureDetails(failure);
    return (
      <main className="product-page is-fandom-home" aria-busy={!failure}>
        <section className={`novel-home-loading ${failure ? "is-failed" : ""}`}>
          <h1>
            {failure
              ? zh
                ? "无法加载"
                : "Unable to load"
              : zh
                ? "正在加载"
                : "Loading"}
          </h1>
          {failure ? (
            <p>{zh ? "刷新重试。" : "Refresh and try again."}</p>
          ) : null}
          {failureDetails && import.meta.env.DEV ? (
            <p className="ui-request-id">{failureDetails}</p>
          ) : null}
        </section>
      </main>
    );
  }

  return (
    <main className="product-page is-fandom-home">
      {fandomGroups.length === 0 ? (
        <section className="novel-home-empty">
          <h2>{zh ? "还没有可读作品" : "No readable Works yet"}</h2>
          <p>
            {zh
              ? "发布作品后，它会直接出现在这里。"
              : "Published Works will appear here directly."}
          </p>
          <div className="novel-home-empty-actions">
            <a className="ui-action ui-action--primary" href="/create">
              {zh ? "发布作品" : "Publish Work"}
            </a>
            <a className="ui-action ui-button--secondary" href="/search">
              {zh ? "发现作品" : "Discover Works"}
            </a>
          </div>
        </section>
      ) : (
        <>
          <FandomShortcutRail
            groups={shortcutGroups}
            language={language}
            preserveOrder={mode === "public"}
            zh={zh}
          />
          <GenreFilterBar
            filters={genreFilters}
            selectedGenre={selectedGenre}
            setSelectedGenre={setSelectedGenre}
            zh={zh}
          />
          <WorkListSection
            language={language}
            mode={mode}
            works={visibleWorks}
            zh={zh}
          />
        </>
      )}
    </main>
  );
}
